import React, { useEffect, useMemo, useState } from 'react';
import { MessageCenterNav } from './components';
import { MessageList, MessageSetting } from './container';
import { useExternalMessage, useFetchMessageNavGroup, useFetchSuiteUnreadStatus } from './hooks';
import { MessageNavType } from './interfaces';
import { Spin } from '@fx-ui/fine-design';
import classNames from 'classnames';
import { tracker } from '../../utils';
import { platformIntl, TRACKER } from '../../constants';
import { map, SuiteId } from '@libs/shared';

interface MessageCenterProps {
    suiteId?: SuiteId;
}

/**
 * 消息中心页面
 */
const MessageCenter: React.FC<MessageCenterProps> = ({ suiteId }) => {
    const {
        isFetchingExternalMessage,
        showExternalMessage,
        hasUnreadExternalMessage,
        renderExternalMessage
    } = useExternalMessage();
    const { currentSuiteId, isFetchingSuiteUnreadStatus, suiteUnreadStatus, switchCurrentSuite } =
        useFetchSuiteUnreadStatus(suiteId);

    // 现在导航项是异步获取的，所以不适合指定默认值，需要等导航项返回后才能继续后面的操作
    const [activeKey, setActiveKey] = useState<MessageNavType>();

    const { isFetchingMessageNavGroup, messageNavGroup, messageFilters, fetchMessageNavGroup } =
        useFetchMessageNavGroup(activeKey);

    const { messageTypeKeySet, platformMessageKeySet } = useMemo(() => {
        const { suite = [], platform = [] } = messageNavGroup ?? {};
        const suiteMessageKeys = map(suite, 'key');
        const platformMessageKeys = map(platform, 'key');

        return {
            messageTypeKeySet: new Set([...suiteMessageKeys, ...platformMessageKeys]),
            platformMessageKeySet: new Set(platformMessageKeys)
        };
    }, [messageNavGroup]);

    useEffect(() => {
        // suiteId 有可能是 platform（值为0）
        if (currentSuiteId !== undefined) {
            fetchMessageNavGroup(currentSuiteId);
        }
    }, [currentSuiteId]);

    const getSuiteIdByNav = (activeKey: MessageNavType) => {
        return platformMessageKeySet.has(activeKey) ? undefined : currentSuiteId;
    };

    const renderMessageContent = () => {
        if (!activeKey) {
            return null;
        }
        switch (activeKey) {
            case MessageNavType.MessageSetting:
                return (
                    <MessageSetting key={MessageNavType.MessageSetting} suiteId={currentSuiteId} />
                );
            case MessageNavType.External:
                return renderExternalMessage(MessageNavType.External);
            default:
                if (!messageTypeKeySet.has(activeKey)) {
                    return null;
                }
                return (
                    <MessageList
                        messageFilters={messageFilters}
                        type={activeKey}
                        expireTip={platformIntl.get('message.expire.tip')}
                        key={activeKey}
                        currentSuiteId={getSuiteIdByNav(activeKey)}
                    />
                );
        }
    };

    if (isFetchingMessageNavGroup || isFetchingSuiteUnreadStatus || isFetchingExternalMessage) {
        return <Spin />;
    }

    return (
        <div className="fx-message-center">
            <div className="message-center-container">
                <MessageCenterNav
                    messageNavGroup={messageNavGroup}
                    suiteUnreadStatus={suiteUnreadStatus}
                    onSuiteChange={switchCurrentSuite}
                    currentSuiteId={currentSuiteId}
                    activeKey={activeKey}
                    showExternalMessage={showExternalMessage}
                    hasUnreadExternalMessage={hasUnreadExternalMessage}
                    onActiveKeyChange={(key) => {
                        if (key === MessageNavType.OperationNotify) {
                            tracker(TRACKER.OPERATION_NOTIFY_CLICK);
                        }
                        setActiveKey(key);
                    }}
                />
                <div
                    className={classNames('message-center-content', {
                        'has-padding':
                            activeKey &&
                            [MessageNavType.MessageSetting, MessageNavType.External].includes(
                                activeKey
                            )
                    })}
                >
                    {renderMessageContent()}
                </div>
            </div>
        </div>
    );
};

export default MessageCenter;
